import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { getEvents, getSnapshotOptions } from '../../../api';
const Changelog = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [options, setOptions] = useState({
    states: [],
    filingTypes: [],
    companies: []
  });
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeType, setActiveType] = useState('All types');
  const [filterState, setFilterState] = useState('All states');
  const [filterCompany, setFilterCompany] = useState('All companies');
  const [filterDays, setFilterDays] = useState('All time');
  const [typeCounts, setTypeCounts] = useState([]);
  const [filingHistory, setFilingHistory] = useState([]);
  const [todayCount, setTodayCount] = useState(0);
  useEffect(() => {
    getSnapshotOptions().then(res => setOptions(res.data.data));
  }, []);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalRows, setTotalRows] = useState(0);
  const eventTypes = [{
    label: 'All types',
    color: 'var(--text2)'
  }, {
    label: 'Approved',
    color: 'var(--c-teal-active)'
  }, {
    label: 'Rejected',
    color: 'var(--c-orange)'
  }, {
    label: 'Objection',
    color: 'var(--c-orange)'
  }, {
    label: 'Response filed',
    color: 'var(--c-teal-active)'
  }, {
    label: 'Status changed',
    color: 'var(--c-blue-1)'
  }, {
    label: 'Document added',
    color: 'var(--c-purple-1)'
  }, {
    label: 'Disposition date',
    color: 'var(--c-teal-active)'
  }];
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await getEvents({
          page: currentPage,
          limit: rowsPerPage,
          search: searchTerm,
          type: activeType,
          state: filterState,
          company: filterCompany,
          days: filterDays === 'All time' ? null : parseInt(filterDays)
        });
        setEvents(response.data.data);
        setTotalRows(response.data.meta.totalItems);
        setTypeCounts(response.data.typeCounts || []);
        setTodayCount(response.data.todayCount || 0);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    const timer = setTimeout(() => {
      fetchEvents();
    }, 300);
    return () => clearTimeout(timer);
  }, [currentPage, rowsPerPage, searchTerm, activeType, filterState, filterCompany, filterDays]);
  const getCountForType = typeLabel => {
    if (typeLabel === 'All types') return totalRows;
    const typeMatch = typeLabel.split(' ')[0].toLowerCase();
    const found = typeCounts.find(tc => tc.type.includes(typeMatch));
    return found ? found.count : 0;
  };
  const getEventColor = type => {
    if (!type) return 'var(--text3)';
    const found = eventTypes.find(t => type.toLowerCase().includes(t.label.split(' ')[0].toLowerCase()));
    return found ? found.color : 'var(--text3)';
  };
  const formatEventType = type => {
    if (!type) return '';
    const found = eventTypes.find(t => type.toLowerCase().includes(t.label.split(' ')[0].toLowerCase()));
    if (found) return found.label;
    return type.charAt(0).toUpperCase() + type.slice(1);
  };
  useEffect(() => {
    if (selectedEventId) {
      const ev = events.find(e => e.id === selectedEventId);
      setSelectedEvent(ev);
    } else {
      setSelectedEvent(null);
    }
  }, [selectedEventId, events]);
  useEffect(() => {
    if (selectedEvent) {
      getEvents({
        tracking_number: selectedEvent.serff_tracking_number,
        limit: 100
      }).then(res => setFilingHistory(res.data.data));
    } else {
      setFilingHistory([]);
    }
  }, [selectedEvent]);
  const handleOpenSerff = () => {
    if (!selectedEvent) return;
    window.open(`https://filingaccess.serff.com/sfa/search/filingSummary.xhtml?trackingNumber=${selectedEvent.serff_tracking_number}`, '_blank');
  };
  const handleViewFiling = () => {
    if (!selectedEvent) return;
    navigate(`/filings?search=${selectedEvent.serff_tracking_number}`);
  };
  const handleViewObjections = () => {
    if (!selectedEvent) return;
    navigate(`/objections?search=${selectedEvent.serff_tracking_number}`);
  };
  const totalPages = Math.ceil(totalRows / rowsPerPage);

  // Group events by date
  const groupedEvents = events.reduce((groups, event) => {
    const date = new Date(event.detected_at).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    if (!groups[date]) groups[date] = [];
    groups[date].push(event);
    return groups;
  }, {});

  // Stats calculation
  const stats = {
    today: todayCount,
    approvals: getCountForType('Approved'),
    rejections: getCountForType('Rejected'),
    objections: getCountForType('Objection'),
    docs: getCountForType('Document')
  };
  if (loading) return <div className="content">Loading change log...</div>;
  return <>
    <div className="topbar">
      <div className="topbar-left">
        <div className="topbar-title">Change log</div>
        <div className="topbar-sub">{events.length} total events detected · Real-time feed</div>
      </div>
      {/* <div className="topbar-right">
          <button className="btn btn-sm btn-export" onClick={handleExport}>Export CSV</button>
          <button className="btn btn-sm btn-primary">Run Pass 2</button>
        </div> */}
    </div>

    <div className="body">
      <div className="feed-area">
        {/* Stats strip */}
        <div className="stats-strip">
          <div className="stat-strip-item">
            <div className="ssl-label">Today</div>
            <div className="ssl-val">{stats.today}</div>
            <div className="ssl-sub">events detected</div>
          </div>
          <div className="stat-strip-item">
            <div className="ssl-label">Approvals</div>
            <div className="ssl-val changelog-stat--approvals">{stats.approvals}</div>
            <div className="ssl-sub">since last run</div>
          </div>
          <div className="stat-strip-item">
            <div className="ssl-label">Rejections</div>
            <div className="ssl-val changelog-stat--rejections">{stats.rejections}</div>
            <div className="ssl-sub">since last run</div>
          </div>
          <div className="stat-strip-item">
            <div className="ssl-label">Objections</div>
            <div className="ssl-val changelog-stat--objections">{stats.objections}</div>
            <div className="ssl-sub">active filings</div>
          </div>
          <div className="stat-strip-item">
            <div className="ssl-label">Doc changes</div>
            <div className="ssl-val changelog-stat--documents">{stats.docs}</div>
            <div className="ssl-sub">additions</div>
          </div>
          <div className="stat-strip-item">
            <div className="ssl-label">Total events</div>
            <div className="ssl-val">{totalRows}</div>
            <div className="ssl-sub">tracked</div>
          </div>
        </div>

        {/* Filters bar */}
        <div className="filters-bar">
          <div className="search-wrap">
            <Search className="search-icon" size={14} />
            <input className="search-input" type="text" placeholder="Search tracking # or company..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          </div>
          <select className="filter-select" value={filterState} onChange={e => {
            setFilterState(e.target.value);
            setCurrentPage(1);
          }}>
            <option value="All states">All states</option>
            {options.states.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="filter-select" value={filterDays} onChange={e => {
            setFilterDays(e.target.value);
            setCurrentPage(1);
          }}>
            <option value="All time">All time</option>
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
          <select className="filter-select" value={filterCompany} onChange={e => {
            setFilterCompany(e.target.value);
            setCurrentPage(1);
          }}>
            <option value="All companies">All companies</option>
            {(options.companies || []).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Type filter chips */}
        <div className="type-filters">
          {eventTypes.map(type => <div key={type.label} className={`type-chip ${activeType === type.label ? 'active' : ''}`} onClick={() => setActiveType(type.label)}>
            <div className="type-chip-dot" style={{
              background: type.color
            }}></div>
            {type.label}
            <span className="type-chip-count">{getCountForType(type.label)}</span>
          </div>)}
        </div>

        {/* Feed */}
        <div className="feed">
          {Object.keys(groupedEvents).map(date => <React.Fragment key={date}>
            <div className="date-group-header">
              {date}
              <span className="dg-count">{groupedEvents[date].length} events</span>
            </div>
            {groupedEvents[date].map(event => <div key={event.id} className={`event-row ${selectedEventId === event.id ? 'active' : ''}`} onClick={() => setSelectedEventId(event.id)}>
              <div className="event-type-col">
                <div className="event-dot" style={{
                  background: getEventColor(event.change_type)
                }}></div>
                <div className="event-time">
                  {new Date(event.detected_at).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
              <div className="event-body">
                <div className="event-header">
                  <span className="event-tn">{event.serff_tracking_number}</span>
                  <span className="event-company">{event.company_name}</span>
                  <span className="event-state">{event.state}</span>
                </div>
                <div className="event-change">
                  <div className="event-change-type">{formatEventType(event.change_type)}</div>
                  <div className="event-values">
                    {event.old_value && <span className="ev-old">{event.old_value}</span>}
                    {event.old_value && <span className="ev-arrow">→</span>}
                    <span className={`ev-new ${event.change_type.toLowerCase().includes('approved') ? 'approved' : event.change_type.toLowerCase().includes('rejected') ? 'rejected' : event.change_type.toLowerCase().includes('objection') ? 'objection' : ''}`}>
                      {event.new_value}
                    </span>
                  </div>
                  {event.notes && <div className="event-notes">{event.notes}</div>}
                </div>
              </div>
              <div className="event-actions">
                <span className={`pill pill-${event.change_type.toLowerCase().includes('approved') ? 'green' : event.change_type.toLowerCase().includes('rejected') ? 'red' : event.change_type.toLowerCase().includes('objection') ? 'amber' : 'blue'}`}>
                  {formatEventType(event.change_type).split(' ')[0]}
                </span>
              </div>
            </div>)}
          </React.Fragment>)}
        </div>

        {/* Pagination */}
        <div className="pagination pagination-sticky">
          <div className="pg-info">
            Showing {totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, totalRows)} of {totalRows} events
          </div>
          <div className="table-pagination">
            <div className="table-pagination__control">
              <span className="table-pagination__label">Rows</span>
              <select className="pg-select" value={rowsPerPage} onChange={e => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(1);
              }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="pg-controls">
              <button className="pg-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>
                <ChevronLeft size={14} />
              </button>
              <div className="pagination__pages">
                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1;
                  if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                    return <button key={pageNum} className={`pg-btn ${currentPage === pageNum ? 'active' : ''}`} onClick={() => setCurrentPage(pageNum)}>
                      {pageNum}
                    </button>;
                  }
                  if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="pagination__ellipsis">…</span>;
                  }
                  return null;
                })}
              </div>
              <button className="pg-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Detail panel */}
      {selectedEvent && <div className="detail-panel">
        <div className="dp-header">
          <div className="dp-close" onClick={() => setSelectedEventId(null)}><X size={18} /></div>
          <div className="dp-tn">{selectedEvent.serff_tracking_number}</div>
          <div className="dp-company">{selectedEvent.company_name}</div>
          <div className="dp-meta">{selectedEvent.state} · SERFF Tracking</div>
        </div>

        <div className="dp-body">
          <div className="dp-section-title">Event detail</div>
          <div className="event-detail-card">
            <div className="edc-type">{formatEventType(selectedEvent.change_type)}</div>
            <div className="edc-time">{new Date(selectedEvent.detected_at).toLocaleString()}</div>
            <div className="value-comparison">
              <div className="vc-row">
                <div className="vc-label">From</div>
                <div className="vc-val vc-old">{selectedEvent.old_value || '—'}</div>
              </div>
              <div className="vc-row">
                <div className="vc-label">To</div>
                <div className={`vc-val vc-new ${selectedEvent.change_type.toLowerCase().includes('approved') ? 'approved' : selectedEvent.change_type.toLowerCase().includes('rejected') ? 'rejected' : ''}`}>
                  {selectedEvent.new_value}
                </div>
              </div>
            </div>
            {selectedEvent.notes && <div className="vc-notes">
              {selectedEvent.notes}
            </div>}
          </div>

          <div className="dp-section-title">Filing summary</div>
          <div className="filing-mini">
            <div className="fm-row">
              <span className="fm-label">Tracking #</span>
              <span className="fm-val changelog-tracking">{selectedEvent.serff_tracking_number}</span>
            </div>
            <div className="fm-row">
              <span className="fm-label">Company</span>
              <span className="fm-val">{selectedEvent.company_name}</span>
            </div>
            <div className="fm-row">
              <span className="fm-label">State</span>
              <span className="fm-val">{selectedEvent.state}</span>
            </div>
            <div className="fm-row">
              <span className="fm-label">Filing type</span>
              <span className="fm-val">{selectedEvent.snapshot?.filing_type || '—'}</span>
            </div>
            <div className="fm-row">
              <span className="fm-label">Submitted</span>
              <span className="fm-val">{selectedEvent.snapshot?.submission_date || '—'}</span>
            </div>
            <div className="fm-row">
              <span className="fm-label">Respond by</span>
              <span className="fm-val changelog-highlight">—</span>
            </div>
            <div className="fm-row">
              <span className="fm-label">Current status</span>
              <span className="pill pill-amber changelog-pill-meta">{selectedEvent.snapshot?.status || '—'}</span>
            </div>
            <div className="fm-row">
              <span className="fm-label">Zip file</span>
              <span className="fm-val" style={{
                color: selectedEvent.snapshot?.last_zip_path ? 'var(--c-teal-active)' : 'var(--text3)'
              }}>
                {selectedEvent.snapshot?.last_zip_path ? 'Downloaded' : 'Not Available'}
              </span>
            </div>
          </div>

          <div className="dp-section-title">All changes for this filing</div>
          <div className="filing-timeline">
            {filingHistory.map((h, i) => <div key={h.id} className="ft-event">
              <div className="ft-dot-col">
                <div className="ft-dot" style={{
                  background: getEventColor(h.change_type)
                }}></div>
                {i < filingHistory.length - 1 && <div className="ft-line"></div>}
              </div>
              <div className="ft-body">
                <div className="ft-time">{new Date(h.detected_at).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric'
                })} · {new Date(h.detected_at).toLocaleTimeString(undefined, {
                  hour: '2-digit',
                  minute: '2-digit'
                })}</div>
                <div className="ft-type">{formatEventType(h.change_type)}</div>
                <div className="ft-detail">{h.old_value || '—'} → {h.new_value}</div>
              </div>
            </div>)}
          </div>
        </div>

        <div className="dp-actions">
          <button className="btn btn-sm" onClick={handleViewFiling}>View filing</button>
          <button className="btn btn-sm" onClick={handleViewObjections}>View objections</button>
          <button className="btn btn-sm btn-primary" onClick={handleOpenSerff}>Open in SERFF</button>
        </div>
      </div>}
    </div>
  </>;
};
export default Changelog;